import highlightSVG from '../assets/img/highlight.svg';
import cursorSVG from '../assets/img/cursor.svg';
import contrastSVG from '../assets/img/contrast.svg';
import moonSVG from '../assets/img/moon.svg';
import sunSVG from '../assets/img/sun.svg';

window.VAE = window.VAE || {};

VAE.Colors = (function () {
  'use strict';

  let subscriptionActive = false;
  let controlSetters = {};

  function initStateManager() {
    if (subscriptionActive) return;
    
    console.log('VAE.Colors: Initializing state manager integration');
    
    if (typeof VAE.StateManager === 'undefined') {
      console.error('VAE.Colors: StateManager not available yet, will retry');
      setTimeout(initStateManager, 100);
      return;
    }
    
    VAE.StateManager.subscribe('colors', handleStateUpdate);
    subscriptionActive = true;
  }

  function handleStateUpdate(colorState) {
    if (!colorState) {
      console.error('VAE.Colors: Received empty state update from StateManager');
      return;
    }
    
    // Default-Werte für Colors
    const DEFAULT_VALUES = {
      highContrast: false,
      highContrastLight: false,
      grayscale: false,
      highlightInteractive: false,
      accessibleCursor: false
    };

    const rootElement = document.documentElement;
    
    // Nur CSS-Klassen setzen wenn Werte von Default abweichen
    const hasNonDefaultContrast = colorState.highContrast !== DEFAULT_VALUES.highContrast ||
                                 colorState.highContrastLight !== DEFAULT_VALUES.highContrastLight ||
                                 colorState.grayscale !== DEFAULT_VALUES.grayscale;

    if (hasNonDefaultContrast) {
      if (colorState.highContrast) {
        rootElement.classList.remove('vae-high-contrast-light', 'vae-grayscale');
        rootElement.classList.add('vae-high-contrast');
      } else if (colorState.highContrastLight) {
        rootElement.classList.remove('vae-high-contrast', 'vae-grayscale');
        rootElement.classList.add('vae-high-contrast-light');
      } else if (colorState.grayscale) {
        rootElement.classList.remove('vae-high-contrast', 'vae-high-contrast-light');
        rootElement.classList.add('vae-grayscale');
      } else {
        rootElement.classList.remove('vae-high-contrast', 'vae-high-contrast-light', 'vae-grayscale');
      }
    }
    
    // Nur bei Non-Default-Werten CSS-Klassen togglen
    if (colorState.highlightInteractive !== DEFAULT_VALUES.highlightInteractive) {
      rootElement.classList.toggle('vae-highlight-interactive', !!colorState.highlightInteractive);
    }
    if (colorState.accessibleCursor !== DEFAULT_VALUES.accessibleCursor) {
      rootElement.classList.toggle('vae-accessible-cursor', !!colorState.accessibleCursor);
    }
    
    // UI Controls immer updaten (da diese keine DOM-Styles beeinflussen)
    if (controlSetters.highContrastDark) {
      controlSetters.highContrastDark(!!colorState.highContrast);
    }
    if (controlSetters.highContrastLight) {
      controlSetters.highContrastLight(!!colorState.highContrastLight);
    }
    if (controlSetters.grayscale) {
      controlSetters.grayscale(!!colorState.grayscale);
    }
    if (controlSetters.highlightInteractive) {
      controlSetters.highlightInteractive(!!colorState.highlightInteractive);
    }
    if (controlSetters.accessibleCursor) {
      controlSetters.accessibleCursor(!!colorState.accessibleCursor);
    }
  }

  function renderSectionContent(container) {
    container.innerHTML = '';
    controlSetters = {};
    
    try {
      const currentState = VAE.StateManager.getState('colors');
      
      const highlightInteractiveSwitch = VAE.SharedUI.createSwitchToggle({
        label: 'Interaktive Elemente hervorheben',
        icon: highlightSVG,
        id: 'vae-highlight-interactive',
        initialChecked: currentState?.highlightInteractive || false,
        onChange: (checked) => {
          setHighlightInteractive(checked);
        }
      });
      
      const accessibleCursorSwitch = VAE.SharedUI.createSwitchToggle({
        label: 'Barrierefreier Mauszeiger',
        icon: cursorSVG,
        id: 'vae-accessible-cursor',
        initialChecked: currentState?.accessibleCursor || false,
        onChange: (checked) => {
          setAccessibleCursor(checked);
        }
      });
      
      const grayscaleSwitch = VAE.SharedUI.createSwitchToggle({
        label: 'Graustufenmodus',
        icon: contrastSVG,
        id: 'vae-grayscale',
        initialChecked: currentState?.grayscale || false,
        onChange: (checked) => {
          setGrayscale(checked);
        }
      });
      
      const highContrastDarkSwitch = VAE.SharedUI.createSwitchToggle({
        label: 'Hoher Kontrast (Dunkel)',
        icon: moonSVG,
        id: 'vae-high-contrast-dark',
        initialChecked: currentState?.highContrast || false,
        onChange: (checked) => {
          setHighContrastDark(checked);
        }
      });
      
      const highContrastLightSwitch = VAE.SharedUI.createSwitchToggle({
        label: 'Hoher Kontrast (Hell)',
        icon: sunSVG,
        id: 'vae-high-contrast-light',
        initialChecked: currentState?.highContrastLight || false,
        onChange: (checked) => {
          setHighContrastLight(checked);
        }
      });

      controlSetters.highlightInteractive = highlightInteractiveSwitch.setValue;
      controlSetters.accessibleCursor = accessibleCursorSwitch.setValue;
      controlSetters.grayscale = grayscaleSwitch.setValue;
      controlSetters.highContrastDark = highContrastDarkSwitch.setValue;
      controlSetters.highContrastLight = highContrastLightSwitch.setValue;

      container.appendChild(highlightInteractiveSwitch.container);
      container.appendChild(accessibleCursorSwitch.container);
      container.appendChild(grayscaleSwitch.container);
      container.appendChild(highContrastDarkSwitch.container);
      container.appendChild(highContrastLightSwitch.container);
    } catch (e) {
      console.error('VAE.Colors: Error rendering tab content:', e);
      container.appendChild(document.createTextNode('Error loading color settings. Please try refreshing the page.'));
    }
  }

  function setHighContrastDark(enabled) {
    const currentState = getCurrentState();
    
    if (enabled === currentState.highContrast) {
      return;
    }
    
    VAE.StateManager.updateState('colors', {
      highContrast: enabled,
      highContrastLight: false,
      grayscale: false
    });
    
  }
  
  function setHighContrastLight(enabled) {
    const currentState = getCurrentState();
    
    if (enabled === currentState.highContrastLight) {
      return;
    }
    
    VAE.StateManager.updateState('colors', {
      highContrast: false,
      highContrastLight: enabled,
      grayscale: false
    });
    
  }
  
  function setGrayscale(enabled) {
    const currentState = getCurrentState();
    
    if (enabled === currentState.grayscale) {
      return;
    }
    
    VAE.StateManager.updateState('colors', {
      highContrast: false,
      highContrastLight: false,
      grayscale: enabled
    });
    
  }
  
  function setHighlightInteractive(enabled) {
    const currentState = getCurrentState();
    
    if (enabled === currentState.highlightInteractive) {
      return;
    }
    
    VAE.StateManager.updateState('colors', { 
      highlightInteractive: enabled 
    });
  }
  
  function setAccessibleCursor(enabled) {
    const currentState = getCurrentState();
    
    if (enabled === currentState.accessibleCursor) {
      return;
    }
    
    VAE.StateManager.updateState('colors', { 
      accessibleCursor: enabled 
    });
  }

  function getCurrentState() {
    try {
      const stateManagerValues = VAE.StateManager.getState('colors');
      if (stateManagerValues) {
        return { ...stateManagerValues };
      }
      
      return {
        highContrast: false,
        highContrastLight: false,
        grayscale: false,
        highlightInteractive: false,
        accessibleCursor: false
      };
    } catch (e) {
      console.error('VAE.Colors: Error getting current state:', e);
      return {
        highContrast: false,
        highContrastLight: false,
        grayscale: false,
        highlightInteractive: false,
        accessibleCursor: false
      };
    }
  }

  function _init() {
    console.log('VAE.Colors: Initializing module');
    
    const initPromise = new Promise((resolve) => {
      function checkStateManager() {
        if (typeof VAE.StateManager !== 'undefined') {
          console.log('VAE.Colors: State manager found, initializing');
          initStateManager();
          resolve();
        } else {
          setTimeout(checkStateManager, 50);
        }
      }
      
      checkStateManager();
    });
    
    Promise.all([initPromise, new Promise(r => setTimeout(r, 0))]).then(() => {
      console.log('VAE.Colors: Module fully initialized');
    });
    
    return true;
  }

  _init();

  return {
    getCurrentState,
    initStateManager,
    renderSectionContent,
  };
})();